import {
  GlobalWorkerOptions,
  getDocument,
} from "pdfjs-new/legacy/build/pdf.min";
import { PDFPageView, EventBus } from "pdfjs-new/legacy/web/pdf_viewer";
import { PDFJS } from "pdfjs-old/web/pdf_viewer";
import config from "../config";

/**
 * The PdfProvider class is responsible for handling the loading and usage of different versions of the PDFJS library.
 * It provides methods to load the library, retrieve PDF documents, get viewport information, and create PDF page views.
 * The class supports compatibility with both the old and new versions of the PDFJS library.
 */
export default class PdfProvider {
  mustUseNewPdf = config.useRecentPdfLibraryVersion;

  /**
   * Retrieves a PDF document from the given URL.
   *
   * @param {string} url - The URL of the PDF document to be retrieved.
   * @return {Promise} A promise that resolves to the PDF document object.
   */
  getPdfDocument(url) {
    if (this.mustUseNewPdf) {
      return getDocument({
        url,
        disableFontFace: this.mustUseNewPdf,
      });
    } else {
      return PDFJS.getDocument({ url });
    }
  }

  /**
   * Retrieves the viewport for a PDF page, scaling it according to the specified zoom level.
   *
   * @param {Object} pdf - The PDF page object from which the viewport will be retrieved.
   * @param {number} zoom - The zoom level to scale the viewport.
   * @return {Object} The viewport object for the specified PDF page with the applied zoom level.
   */
  getPdfPageViewPort(pdf, zoom) {
    if (this.mustUseNewPdf) {
      return pdf.getViewport({ scale: zoom });
    } else {
      return pdf.getViewport(zoom);
    }
  }

  /**
   * Creates a PDF page view using the specified parameters.
   *
   * @param {Object} options - The configuration options for creating the PDF page view.
   * @param {HTMLElement} options.container - The container element in which the page view will be rendered.
   * @param {number} options.id - The unique identifier for the PDF page.
   * @param {number} options.scale - The scale to be applied to the PDF page view.
   * @param {Object} options.defaultViewport - The default viewport defining the initial size and position of the page view.
   * @return {Object} Returns an instance of the PDFPageView class configured with the given parameters.
   */
  createPdfPageView({ container, id, scale, defaultViewport }) {
    if (this.mustUseNewPdf) {
      return new PDFPageView({
        container,
        id,
        scale,
        defaultViewport,
        eventBus: new EventBus(),
      });
    } else {
      return new PDFJS.PDFPageView({
        container,
        id,
        scale,
        defaultViewport,
      });
    }
  }

  /**
   * Loads the necessary PDF.js worker file based on the configuration.
   * This method checks whether the new or old PDF.js worker is required,
   * and dynamically sets the appropriate worker source.
   *
   * @return {Promise<void>} A promise that resolves when the PDF.js worker has been successfully loaded.
   */
  async loadPdfJsWorker() {
    return new Promise((resolve) => {
      require.ensure(
        [],
        (require) => {
          if (this.mustUseNewPdf) {
            GlobalWorkerOptions.workerSrc = require("file-loader!pdfjs-new/legacy/build/pdf.worker.min");
          } else {
            PDFJS.workerSrc = require("file-loader!pdfjs-old/build/pdf.worker.min");
          }
          resolve();
        },
        "pdf"
      );
    });
  }
}



// WEBPACK FOOTER //
// ./src/common/components/pdfProvider.js